// Nearby Service
//
// People close to where the viewer actually is right now, from /feed/nearby.
// Replaces the hardcoded list of invented users the tab used to generate.
//
// Location is sent with the request rather than read off the profile: someone
// signing in from another city should see that city. When the browser refuses
// or cannot supply a position, the request omits the coordinates and the
// backend falls back to the stored profile location (get_nearby_candidates,
// migration 044).

import { FeedItem } from '../../types/socialFeed';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { getBrowserLocation } from '../../utils/Device/geolocation';
import { logger } from '../../utils/logger';

const API_BASE = `${config.api.baseUrl}/feed`;

function headers(): HeadersInit {
  return { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
}

/**
 * One 401/403 recovery attempt, for the same reason moments and explore do it:
 * the stored access token can go stale while supabase-js still holds a valid
 * session, and a direct `fetch` never passes through authInterceptor's response
 * hook that would otherwise repair it.
 */
async function request(path: string): Promise<any> {
  const send = () => fetch(`${API_BASE}${path}`, { headers: headers() });

  let response = await send();

  if (response.status === 401 || response.status === 403) {
    logger.log('[NEARBY] Token rejected, refreshing session and retrying');
    try {
      await authInterceptor.performRefresh();
      response = await send();
    } catch (error) {
      logger.error('[NEARBY] Token refresh failed:', error);
    }
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body?.message || body?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'Your session expired. Please sign in again.'
        : `${detail || 'Request failed'} (HTTP ${response.status})`,
    );
  }

  const body = await response.json();
  return body.data;
}

export interface NearbyPosition {
  latitude: number;
  longitude: number;
}

/**
 * The viewer's current position, or null if unavailable. Resolves null rather
 * than throwing on a denied permission — the feed still works from the stored
 * profile location, so a refusal must not be fatal.
 */
export async function getCurrentPosition(): Promise<NearbyPosition | null> {
  try {
    return await getBrowserLocation();
  } catch (error) {
    logger.log('[NEARBY] Browser location unavailable, using profile location');
    return null;
  }
}

/** Stable ordering for one scroll session, so offset paging cannot repeat. */
export function newNearbySeed(): string {
  return Math.random().toString(36).slice(2, 10);
}

class NearbyService {
  async getNearbyFeed(
    options: { position?: NearbyPosition | null; limit?: number; offset?: number; seed?: string } = {},
  ): Promise<FeedItem[]> {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 20),
      offset: String(options.offset ?? 0),
    });
    if (options.seed) params.set('seed', options.seed);
    // Omitted entirely when unknown, which is what makes the backend fall back
    // to the profile location instead of receiving empty strings.
    if (options.position) {
      params.set('lat', String(options.position.latitude));
      params.set('lng', String(options.position.longitude));
    }

    const data = await request(`/nearby?${params}`);
    return (data ?? []) as FeedItem[];
  }
}

export const nearbyService = new NearbyService();
